import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, BACKEND_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import {
  Zap, Droplets, Users, Shield, BarChart3, AlertTriangle,
  Plus, X, TrendingUp, Lightbulb, TrendingDown, Star,
  CheckCircle, Loader2
} from "lucide-react";

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function SmartAutomationPage() {
  const [autoPayRules, setAutoPayRules] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [insights, setInsights] = useState(null);
  const [groups, setGroups] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [securitySettings, setSecuritySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("automation");
  const [showAddRule, setShowAddRule] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddMeter, setShowAddMeter] = useState(false);
  const [showCreateSplit, setShowCreateSplit] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [ruleForm, setRuleForm] = useState({ meter_number: "", provider_code: "ESCOM", rule_name: "", trigger_type: "low_balance", payment_amount: 5000, balance_threshold: 50 });
  const [groupForm, setGroupForm] = useState({ name: "", group_type: "household", description: "", split_method: "equal" });
  const [beneficiaryForm, setBeneficiaryForm] = useState({ meter_number: "", provider_code: "ESCOM", nickname: "" });
  const [memberForm, setMemberForm] = useState({ name: "", phone: "", share_percentage: "" });
  const [meterForm, setMeterForm] = useState({ meter_number: "", provider_code: "ESCOM", name: "" });
  const [splitForm, setSplitForm] = useState({ meter_number: "", provider_code: "ESCOM", total_amount: "", due_date: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesRes, insightsRes, groupsRes, beneficiariesRes, alertsRes, settingsRes] = await Promise.all([
        api.get("/automation/auto-pay-rules"),
        api.get("/insights/dashboard"),
        api.get("/groups"),
        api.get("/beneficiaries"),
        api.get("/security/fraud-alerts"),
        api.get("/security/settings"),
      ]);
      setAutoPayRules(rulesRes.data || []);
      setInsights(insightsRes.data);
      setGroups(groupsRes.data || []);
      setBeneficiaries(beneficiariesRes.data || []);
      setFraudAlerts(alertsRes.data?.filter(a => !a.is_resolved) || []);
      setSecuritySettings(settingsRes.data || {});
    } catch (err) {
      console.error("Failed to load smart automation data:", err);
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const createAutoPayRule = async () => {
    try {
      await api.post("/automation/auto-pay-rules", null, { params: ruleForm });
      toast.success("Auto-pay rule created");
      setShowAddRule(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to create rule");
    }
  };

  const toggleRule = async (ruleId) => {
    try {
      await api.put(`/automation/auto-pay-rules/${ruleId}/toggle`);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to update rule");
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm("Delete this auto-pay rule?")) return;
    try {
      await api.delete(`/automation/auto-pay-rules/${ruleId}`);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to delete rule");
    }
  };

  const createGroup = async () => {
    try {
      await api.post("/groups", null, { params: groupForm });
      toast.success("Group created");
      setShowAddGroup(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to create group");
    }
  };

  const openAddMember = (groupId) => {
    setActiveGroupId(groupId);
    setMemberForm({ name: "", phone: "", share_percentage: "" });
    setShowAddMember(true);
  };

  const openAddMeter = (groupId) => {
    setActiveGroupId(groupId);
    setMeterForm({ meter_number: "", provider_code: "ESCOM", name: "" });
    setShowAddMeter(true);
  };

  const openCreateSplit = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    const firstMeter = group?.meters?.[0];
    setActiveGroupId(groupId);
    setSplitForm({
      meter_number: firstMeter?.meter_number || "",
      provider_code: firstMeter?.provider_code || "ESCOM",
      total_amount: "",
      due_date: ""
    });
    setShowCreateSplit(true);
  };

  const addGroupMember = async () => {
    if (!memberForm.name || !memberForm.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      const params = { name: memberForm.name, phone: memberForm.phone };
      if (memberForm.share_percentage !== "") params.share_percentage = parseFloat(memberForm.share_percentage);
      await api.post(`/groups/${activeGroupId}/members`, null, { params });
      toast.success("Member added");
      setShowAddMember(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to add member");
    }
  };

  const addGroupMeter = async () => {
    if (!meterForm.meter_number || !meterForm.name) {
      toast.error("Meter number and name are required");
      return;
    }
    try {
      await api.post(`/groups/${activeGroupId}/meters`, null, { params: meterForm });
      toast.success("Meter added");
      setShowAddMeter(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to add meter");
    }
  };

  const createSplitBill = async () => {
    if (!splitForm.meter_number || !splitForm.total_amount) {
      toast.error("Meter number and amount are required");
      return;
    }
    try {
      const params = {
        meter_number: splitForm.meter_number,
        provider_code: splitForm.provider_code,
        total_amount: parseFloat(splitForm.total_amount),
      };
      if (splitForm.due_date) params.due_date = splitForm.due_date;
      await api.post(`/groups/${activeGroupId}/contributions`, null, { params });
      toast.success("Split bill created");
      setShowCreateSplit(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to create split bill");
    }
  };

  const addBeneficiary = async () => {
    try {
      await api.post("/beneficiaries", null, { params: beneficiaryForm });
      toast.success("Beneficiary added");
      setShowAddBeneficiary(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to add beneficiary");
    }
  };

  const updateSecuritySettings = async (updates) => {
    try {
      await api.put("/security/settings", null, { params: updates });
      setSecuritySettings(prev => ({ ...prev, ...updates }));
      toast.success("Settings updated");
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to update settings");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  const tabs = [
    { id: "automation", label: "Smart Automation", icon: Zap },
    { id: "groups", label: "Group Payments", icon: Users },
    { id: "security", label: "Security & Trust", icon: Shield },
    { id: "insights", label: "Usage Insights", icon: BarChart3 }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="smart-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Let SmartPay handle the boring bits</h1>
        <p className="text-muted-foreground">Automation, insights, and fraud protection</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${activeTab === tab.id ? "bg-emerald-500 text-white" : "bg-card border border-border/50 hover:bg-muted"}`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Fraud Alerts Banner */}
      {fraudAlerts.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div className="flex-1">
              <p className="font-semibold text-red-500">{fraudAlerts.length} Security Alert(s)</p>
              <p className="text-sm text-muted-foreground">{fraudAlerts[0]?.description}</p>
            </div>
            <button onClick={() => setActiveTab("security")} className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm">Review</button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "automation" && (
        <div className="space-y-6">
          {/* Auto-Pay Rules */}
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold flex items-center gap-2"><Zap className="w-5 h-5 text-yellow-500" /> Auto-Pay Rules</h2>
              <button onClick={() => setShowAddRule(true)} className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-lg text-sm" data-testid="add-autopay-rule-btn"><Plus className="w-4 h-4" /> Add Rule</button>
            </div>
            {autoPayRules.length === 0 ? (
              <div className="text-center py-8">
                <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium">No auto-pay rules yet</p>
                <p className="text-sm text-muted-foreground">Set up rules to automatically pay when balance is low</p>
              </div>
            ) : (
              <div className="space-y-3">
                {autoPayRules.map(rule => (
                  <div key={rule.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${rule.is_active ? "bg-emerald-500" : "bg-gray-400"}`} />
                      <div>
                        <p className="font-medium">{rule.rule_name}</p>
                        <p className="text-xs text-muted-foreground">{rule.meter_number} - Pay MWK {rule.payment_amount?.toLocaleString()} when balance &lt; {rule.balance_threshold} units</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleRule(rule.id)} className={`px-3 py-1 rounded-lg text-sm ${rule.is_active ? "bg-emerald-500/20 text-emerald-500" : "bg-muted"}`}>
                        {rule.is_active ? "Active" : "Paused"}
                      </button>
                      <button onClick={() => deleteRule(rule.id)} className="p-1 hover:bg-red-500/20 rounded text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bill Predictions */}
          {insights?.summary && (
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <h2 className="font-semibold flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-blue-500" /> Spending Predictions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-500">MWK {(insights.summary.this_month_total || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">This Month</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-blue-500">MWK {(insights.summary.last_month_total || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Last Month</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className={`text-2xl font-bold ${insights.summary.change_percentage > 0 ? "text-red-500" : "text-emerald-500"}`}>
                    {insights.summary.change_percentage > 0 ? "+" : ""}{insights.summary.change_percentage?.toFixed(1)}%
                  </p>
                  <p className="text-xs text-muted-foreground">Change</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{insights.summary.transaction_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Transactions</p>
                </div>
              </div>
            </div>
          )}

          {/* Cost Saving Tips */}
          {insights?.tips && insights.tips.length > 0 && (
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <h2 className="font-semibold flex items-center gap-2 mb-4"><Lightbulb className="w-5 h-5 text-yellow-500" /> Smart Tips</h2>
              <div className="space-y-3">
                {insights.tips.map((tip, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${tip.type === "warning" ? "bg-orange-500/10" : tip.type === "success" ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                    {tip.type === "success" ? <TrendingDown className="w-5 h-5 text-emerald-500 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />}
                    <div>
                      <p className="font-medium">{tip.title}</p>
                      <p className="text-sm text-muted-foreground">{tip.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "groups" && (
        <div className="space-y-6">
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-blue-500" /> Payment Groups</h2>
              <button onClick={() => setShowAddGroup(true)} className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-lg text-sm" data-testid="create-group-btn"><Plus className="w-4 h-4" /> Create Group</button>
            </div>
            {groups.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium">No payment groups</p>
                <p className="text-sm text-muted-foreground">Create a group to split bills with roommates, family, or community</p>
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map(group => (
                  <div key={group.id} className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">{group.name}</p>
                        <p className="text-xs text-muted-foreground">{group.group_type} - {group.members?.length || 0} members - {group.split_method} split</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-500">Active</span>
                    </div>
                    {group.description && <p className="text-sm text-muted-foreground mb-2">{group.description}</p>}
                    {group.meters?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {group.meters.map((m, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500">{m.name} - {m.meter_number}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => openAddMember(group.id)} className="text-xs px-2 py-1 bg-muted rounded hover:bg-muted/70">Add Member</button>
                      <button onClick={() => openAddMeter(group.id)} className="text-xs px-2 py-1 bg-muted rounded hover:bg-muted/70">Add Meter</button>
                      <button onClick={() => openCreateSplit(group.id)} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-500 rounded hover:bg-blue-500/30">Create Split Bill</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "security" && (
        <div className="space-y-6">
          {/* Fraud Alerts */}
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <h2 className="font-semibold flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-red-500" /> Security Alerts</h2>
            {fraudAlerts.length === 0 ? (
              <div className="text-center py-6 text-emerald-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                <p className="font-medium">All Clear!</p>
                <p className="text-sm text-muted-foreground">No security issues detected</p>
              </div>
            ) : (
              <div className="space-y-3">
                {fraudAlerts.map(alert => (
                  <div key={alert.id} className={`p-3 rounded-lg border ${alert.risk_level === "high" ? "bg-red-500/10 border-red-500/30" : alert.risk_level === "medium" ? "bg-orange-500/10 border-orange-500/30" : "bg-yellow-500/10 border-yellow-500/30"}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{alert.alert_type.replace("_", " ").toUpperCase()}</p>
                        <p className="text-sm text-muted-foreground">{alert.description}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${alert.risk_level === "high" ? "bg-red-500 text-white" : alert.risk_level === "medium" ? "bg-orange-500 text-white" : "bg-yellow-500 text-black"}`}>
                        {alert.risk_level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Trusted Beneficiaries */}
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold flex items-center gap-2"><Star className="w-5 h-5 text-yellow-500" /> Trusted Beneficiaries</h2>
              <button onClick={() => setShowAddBeneficiary(true)} className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-lg text-sm" data-testid="add-beneficiary-btn"><Plus className="w-4 h-4" /> Add</button>
            </div>
            {beneficiaries.length === 0 ? (
              <p className="text-sm text-muted-foreground">Add trusted meter numbers to avoid fraud alerts</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {beneficiaries.map(b => (
                  <div key={b.id} className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium">{b.nickname}</p>
                      <p className="text-xs text-muted-foreground">{b.meter_number}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Security Settings */}
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <h2 className="font-semibold flex items-center gap-2 mb-4"><Shield className="w-5 h-5 text-emerald-500" /> Security Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Confirm large transactions</p>
                  <p className="text-xs text-muted-foreground">Require confirmation for transactions above MWK {(securitySettings.require_confirmation_above || 50000).toLocaleString()}</p>
                </div>
                <input type="number" value={securitySettings.require_confirmation_above || 50000}
                  onChange={(e) => updateSecuritySettings({ require_confirmation_above: parseFloat(e.target.value) })}
                  className="w-32 p-2 bg-background border border-border/50 rounded-lg text-right" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Daily transaction limit</p>
                  <p className="text-xs text-muted-foreground">Maximum transactions per day</p>
                </div>
                <input type="number" value={securitySettings.max_daily_transactions || 10}
                  onChange={(e) => updateSecuritySettings({ max_daily_transactions: parseInt(e.target.value) })}
                  className="w-24 p-2 bg-background border border-border/50 rounded-lg text-right" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Daily spending limit</p>
                  <p className="text-xs text-muted-foreground">Maximum amount per day</p>
                </div>
                <input type="number" value={securitySettings.max_daily_amount || 500000}
                  onChange={(e) => updateSecuritySettings({ max_daily_amount: parseFloat(e.target.value) })}
                  className="w-36 p-2 bg-background border border-border/50 rounded-lg text-right" />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "insights" && insights && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <h2 className="font-semibold mb-4">Spending by Provider</h2>
              <div className="space-y-3">
                {Object.entries(insights.by_provider || {}).map(([provider, data]) => (
                  <div key={provider} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {provider.toLowerCase().includes("escom") ? <Zap className="w-4 h-4 text-yellow-500" /> : <Droplets className="w-4 h-4 text-blue-500" />}
                      <span>{provider}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">MWK {data.total?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{data.count} payments</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border/50 rounded-xl p-4">
              <h2 className="font-semibold mb-4">Quick Stats</h2>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Spent (All Time)</span><span className="font-semibold">MWK {insights.summary?.total_spent_all_time?.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Average Transaction</span><span className="font-semibold">MWK {insights.summary?.average_transaction?.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Transactions</span><span className="font-semibold">{insights.summary?.transaction_count}</span></div>
              </div>
            </div>
          </div>

          {/* Low Balance Meters */}
          {insights.low_balance_meters?.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
              <h2 className="font-semibold flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-orange-500" /> Low Balance Meters</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {insights.low_balance_meters.map((meter, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <div>
                      <p className="font-medium">{meter.meter_number}</p>
                      <p className="text-xs text-muted-foreground">{meter.provider_code}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-orange-500">{meter.balance_units?.toFixed(1)} units</p>
                      <p className="text-xs text-muted-foreground">~{meter.estimated_days_remaining || "?"} days</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Rule Modal */}
      <AnimatePresence>
        {showAddRule && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Create Auto-Pay Rule</h3>
              <div className="space-y-4">
                <input placeholder="Rule Name (e.g., Home Electricity)" value={ruleForm.rule_name} onChange={e => setRuleForm({...ruleForm, rule_name: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Meter Number" value={ruleForm.meter_number} onChange={e => setRuleForm({...ruleForm, meter_number: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <select value={ruleForm.provider_code} onChange={e => setRuleForm({...ruleForm, provider_code: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="ESCOM">ESCOM (Electricity)</option>
                  <option value="LLWB">Lilongwe Water Board</option>
                </select>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">When balance below</label>
                    <input type="number" placeholder="50" value={ruleForm.balance_threshold} onChange={e => setRuleForm({...ruleForm, balance_threshold: parseFloat(e.target.value)})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Pay amount (MWK)</label>
                    <input type="number" placeholder="5000" value={ruleForm.payment_amount} onChange={e => setRuleForm({...ruleForm, payment_amount: parseFloat(e.target.value)})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddRule(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={createAutoPayRule} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Create Rule</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Group Modal */}
      <AnimatePresence>
        {showAddGroup && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Create Payment Group</h3>
              <div className="space-y-4">
                <input placeholder="Group Name" value={groupForm.name} onChange={e => setGroupForm({...groupForm, name: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <select value={groupForm.group_type} onChange={e => setGroupForm({...groupForm, group_type: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="household">Household</option>
                  <option value="roommates">Roommates</option>
                  <option value="community">Community</option>
                  <option value="business">Business</option>
                </select>
                <select value={groupForm.split_method} onChange={e => setGroupForm({...groupForm, split_method: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="equal">Split Equally</option>
                  <option value="percentage">By Percentage</option>
                </select>
                <textarea placeholder="Description (optional)" value={groupForm.description} onChange={e => setGroupForm({...groupForm, description: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" rows={2} />
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddGroup(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={createGroup} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Create Group</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Member Modal */}
      <AnimatePresence>
        {showAddMember && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Member</h3>
              <div className="space-y-4">
                <input placeholder="Name" value={memberForm.name} onChange={e => setMemberForm({...memberForm, name: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Phone Number" value={memberForm.phone} onChange={e => setMemberForm({...memberForm, phone: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <div>
                  <label className="text-xs text-muted-foreground">Share percentage (optional, for percentage split)</label>
                  <input type="number" placeholder="e.g. 25" value={memberForm.share_percentage} onChange={e => setMemberForm({...memberForm, share_percentage: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddMember(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={addGroupMember} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Add Member</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Meter Modal */}
      <AnimatePresence>
        {showAddMeter && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Meter to Group</h3>
              <div className="space-y-4">
                <input placeholder="Meter Name (e.g., House Electricity)" value={meterForm.name} onChange={e => setMeterForm({...meterForm, name: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Meter Number" value={meterForm.meter_number} onChange={e => setMeterForm({...meterForm, meter_number: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <select value={meterForm.provider_code} onChange={e => setMeterForm({...meterForm, provider_code: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="ESCOM">ESCOM (Electricity)</option>
                  <option value="LWB">Lilongwe Water Board</option>
                  <option value="BWB">Blantyre Water Board</option>
                  <option value="NRWB">Northern Region Water Board</option>
                </select>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddMeter(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={addGroupMeter} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Add Meter</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Split Bill Modal */}
      <AnimatePresence>
        {showCreateSplit && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Create Split Bill</h3>
              {(() => {
                const group = groups.find(g => g.id === activeGroupId);
                const meters = group?.meters || [];
                return meters.length > 0 ? (
                  <div className="space-y-4">
                    <select
                      value={`${splitForm.meter_number}|${splitForm.provider_code}`}
                      onChange={e => {
                        const [meter_number, provider_code] = e.target.value.split("|");
                        setSplitForm({...splitForm, meter_number, provider_code});
                      }}
                      className="w-full p-2 bg-background border border-border/50 rounded-lg">
                      {meters.map((m, i) => (
                        <option key={i} value={`${m.meter_number}|${m.provider_code}`}>{m.name} - {m.meter_number}</option>
                      ))}
                    </select>
                    <div>
                      <label className="text-xs text-muted-foreground">Total amount (MWK)</label>
                      <input type="number" placeholder="e.g. 20000" value={splitForm.total_amount} onChange={e => setSplitForm({...splitForm, total_amount: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Due date (optional)</label>
                      <input type="date" value={splitForm.due_date} onChange={e => setSplitForm({...splitForm, due_date: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                    </div>
                    <p className="text-xs text-muted-foreground">Split among {group.members?.length || 0} member(s) using the group's {group.split_method} split method.</p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">This group has no meters yet. Add a meter first before creating a split bill.</p>
                  </div>
                );
              })()}
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowCreateSplit(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={createSplitBill} disabled={!groups.find(g => g.id === activeGroupId)?.meters?.length} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">Create Split Bill</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Beneficiary Modal */}
      <AnimatePresence>
        {showAddBeneficiary && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Trusted Beneficiary</h3>
              <div className="space-y-4">
                <input placeholder="Nickname (e.g., Home, Office)" value={beneficiaryForm.nickname} onChange={e => setBeneficiaryForm({...beneficiaryForm, nickname: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <input placeholder="Meter Number" value={beneficiaryForm.meter_number} onChange={e => setBeneficiaryForm({...beneficiaryForm, meter_number: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg" />
                <select value={beneficiaryForm.provider_code} onChange={e => setBeneficiaryForm({...beneficiaryForm, provider_code: e.target.value})} className="w-full p-2 bg-background border border-border/50 rounded-lg">
                  <option value="ESCOM">ESCOM</option>
                  <option value="LLWB">Lilongwe Water Board</option>
                </select>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowAddBeneficiary(false)} className="flex-1 py-2 bg-muted rounded-lg">Cancel</button>
                <button onClick={addBeneficiary} className="flex-1 py-2 bg-emerald-500 text-white rounded-lg">Add Beneficiary</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}